import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { Steps, Tooltip, Typography } from 'antd';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import { type FC, useState } from 'react';
import type { ManagedTrackerMessages } from '../../../store/trackers/trackers_reducers';
import { RelativeTime, TimestampEditor, TooltipWithUTCDatetime } from '../../BasicComponents';
import { IconEditPen } from '../../Icon';
import {
  type TRACKER_MESSAGE_HISTORY_RECORD_STATE_EXTENDED,
  TRACKER_MESSAGE_HISTORY_RECORD_STATE_FRONTEND,
  generateMessageSteps,
  getMessageHistoryRecordInfo,
} from './TrackerMessagesTableHelper';

const { Text } = Typography;

interface Props {
  message: Immutable<ManagedTrackerMessages>;
  onChange: (message: Immutable<ManagedTrackerMessages>) => void;
}

export const TrackerMessageDetails: FC<Props> = ({ message, onChange }) => {
  const payload = message.payload != null ? message.payload.toString('utf-8') : null;
  const { Step } = Steps;
  const [showPlannedAtModal, setShowPlannedAtModal] = useState(false);
  const [showCloseAfterModal, setShowCloseAfterModal] = useState(false);

  const handleOpenModal = (state: TRACKER_MESSAGE_HISTORY_RECORD_STATE_EXTENDED) => {
    if (state === TRACKER_MESSAGE_HISTORY_RECORD_STATE_FRONTEND.PLANNED_FOR) {
      setShowCloseAfterModal(false);
      setShowPlannedAtModal(!showPlannedAtModal);
    } else if (state === TRACKER_MESSAGE_HISTORY_RECORD_STATE_FRONTEND.CLOSE) {
      setShowCloseAfterModal(!showCloseAfterModal);
      setShowPlannedAtModal(false);
    }
  };

  return (
    <Container>
      <CommandLabel>{message.label} Command</CommandLabel>
      <MessageAttempt>
        <Info strong>{'Raw Message:'}</Info>
        <Tooltip title={payload}>
          <Text ellipsis={true}>{payload}</Text>
        </Tooltip>
        {payload && <CopyButton copyable={{ text: payload }} />}
        <AttemptBlock>
          <Info strong>{'Max Number Of Attempts:'}</Info>
          <Text>{message.maxNumOfAttempts}</Text>
        </AttemptBlock>
      </MessageAttempt>
      <MessageHistoryContainer>
        <Steps direction="vertical">
          {generateMessageSteps(message).map((step) => {
            const stylingInfo = getMessageHistoryRecordInfo(step.state);

            return (
              <Step
                icon={stylingInfo?.icon}
                key={step.state}
                title={
                  <div style={{ color: stylingInfo?.color }}>
                    {step.title}
                    {step.date instanceof Date && (
                      <span>
                        <Time time={step.date.toISOString()} />
                        {(step.state ===
                          TRACKER_MESSAGE_HISTORY_RECORD_STATE_FRONTEND.PLANNED_FOR ||
                          step.state === TRACKER_MESSAGE_HISTORY_RECORD_STATE_FRONTEND.CLOSE) && (
                          <TrackerMessageSettingButton
                            onClick={() => handleOpenModal(step.state)}
                            type="button"
                          >
                            <IconEditPen style={{ marginLeft: '5px' }} fixedWidth />
                          </TrackerMessageSettingButton>
                        )}
                      </span>
                    )}
                  </div>
                }
                status="process"
                description={
                  <div style={{ color: stylingInfo?.color }}>
                    {step.description}
                    {step.state === TRACKER_MESSAGE_HISTORY_RECORD_STATE_FRONTEND.PLANNED_FOR &&
                      showPlannedAtModal && (
                        <TimestampEditor
                          timestamp={message.plannedAt.toISOString()}
                          label={'Planned at'}
                          minDate={DateTime.now()}
                          setTimestamp={(timestamp: string) => {
                            onChange({
                              ...message,
                              plannedAt: new Date(timestamp),
                            });
                            setShowPlannedAtModal(false);
                          }}
                        />
                      )}
                    {step.state === TRACKER_MESSAGE_HISTORY_RECORD_STATE_FRONTEND.CLOSE &&
                      showCloseAfterModal && (
                        <TimestampEditor
                          timestamp={message.closeAfter.toISOString()}
                          label={'Close after'}
                          minDate={DateTime.fromJSDate(message.plannedAt)}
                          setTimestamp={(timestamp: string) => {
                            onChange({
                              ...message,
                              closeAfter: new Date(timestamp),
                            });
                            setShowCloseAfterModal(false);
                          }}
                        />
                      )}
                  </div>
                }
              />
            );
          })}
        </Steps>
      </MessageHistoryContainer>
    </Container>
  );
};

const CopyButton = styled(Text)`
  padding: 0 10px 0 5px;
`;

const Info = styled(Text)`
  padding-right: 10px;
`;

const AttemptBlock = styled.div`
  display: flex;
  padding-left: 10px;
  margin-left: auto;
`;

const Time: FC<{ time: string }> = ({ time }) => {
  return (
    <TooltipWithUTCDatetime datetime={time}>
      <TimeField>
        <RelativeTime time={time} />
      </TimeField>
    </TooltipWithUTCDatetime>
  );
};
const CommandLabel = styled.h5`
  margin-bottom: 15px;
`;

const MessageAttempt = styled.div`
  display: flex;
  border: solid 1px ${RacemapColors.CloudBlue};
  padding: 7px;
  margin-bottom: 15px;
  border-radius: 5px;
`;

const TimeField = styled.span`
  font-weight: 300;
  opacity: 0.7;
  font-size: 0.9rem;
  padding-left: 5px;
`;

const Container = styled.div`
  overflow-y: auto;
  background-color: white;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px;
  justify-content: space-between;
`;

const MessageHistoryContainer = styled.div`
  border: solid 1px ${RacemapColors.CloudBlue};
  padding: 20px 20px 0 20px;
  border-radius: 5px;
  min-height: 100px;
  overflow-y: auto;
`;

const TrackerMessageSettingButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${RacemapColors.DarkGray};
`;
